import React, { FC } from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';

import { Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { PetType } from '@rover/types/src/Pet';

import AppStoreCallout from '../../shared/AppStoreCallout/AppStoreCallout';
import LandingSearchResultsCarousel from '../components/LandingSearchResultsCarousel';
import { getSearchFiltersFromSeoLocationDetails } from '../utilities';

import useFacts from './components/Footer/ServiceFaqs/useFacts';
import QuickFact, { QuickFactHighlight } from './components/QuickFact';
import SeoLocationHero from './components/SeoLocationHero';
import SeoLocationReviewsCarousel from './components/SeoLocationReviewsCarousel';
import onValuePropFilterOptionsCtaClick from './utils/onValuePropFilterOptionsCtaClick';
import {
  SeoHowBrandWorks,
  SeoLocationFaqList,
  SeoLocationFooterLinks,
  SeoLocationPageWrapper,
  SeoLocationRoverProtect,
  ValuePropFilterOptions,
  WhyChooseBrand,
} from './components';
import { quickFactBackgroundDoodle } from './images';
import { SeoLocationPageProps } from './types';

const StyledSeoText = styled.div`
  a {
    text-decoration: underline;
  }
`;

const SeoLocationPageTemplate: FC<SeoLocationPageProps> = ({
  seoLocation,
  countryConfig,
  frontendConfig,
  seoLocationDescriptor,
  reviews,
  serviceTypes,
  searchResponse,
}) => {
  const { linkedLocations, details, localityType } = seoLocation;
  const { language } = frontendConfig.settings;
  const initialSearchFilters = getSearchFiltersFromSeoLocationDetails(seoLocation);
  const {
    cityName,
    breadcrumbData,
    relatedServices,
    serviceTypeSlug,
    internalServiceType,
    petType,
    h2,
    facts,
    name: locationName,
  } = details;

  const {
    totalCustomersNum,
    medianListPricePerUnitLocalCurrencyNum,
    avgRepeatCustomerAmongSittersWithRepeatNum,
  } = useFacts(facts);
  // DEV-150772 update name to lowerCaseServiceTypeName. This was a temporary workaround for translations
  const serviceTypeName = details.serviceTypeName.toLocaleLowerCase(language);

  // The price unit varies per service type and comes pre-localized from the backend
  // (e.g. "walk", "night", "visit", "day"), so we interpolate it into a single sentence.
  const renderMedianCostFact = (formattedAmount: string): JSX.Element => (
    <Trans>
      The median cost per {internalServiceType.alternativeRateUnitTranslated} for {serviceTypeName}{' '}
      in {locationName} is <QuickFactHighlight>{formattedAmount}</QuickFactHighlight>
    </Trans>
  );

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <Flex flexDirection="column" alignItems="center">
          <SeoLocationHero
            subheading={h2}
            petType={petType}
            internalServiceTypeName={internalServiceType.name}
            serviceTypeName={details.serviceTypeName}
            locationName={locationName}
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
            showTrustpilot
          />
        </Flex>
      </Section>

      {totalCustomersNum &&
        (() => {
          const { asNumber: ownerCount } = totalCustomersNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Over <QuickFactHighlight>{ownerCount}</QuickFactHighlight> pet parents in{' '}
                {locationName} have booked {serviceTypeName}
              </Trans>
            </QuickFact>
          );
        })()}

      <Section paddingLeft="4x" my={['16x', '12x']}>
        <LandingSearchResultsCarousel
          searchResponse={searchResponse}
          searchFilters={initialSearchFilters}
          language={language}
          heading={t`${locationName}, Find top rated ${serviceTypeName}`}
          // DEV-144415 - once default is updated remove this prop
          headingVariant="heading"
          headingSize="400"
          serviceTypeSlug={serviceTypeSlug}
          countryConfig={countryConfig}
        />
      </Section>

      {medianListPricePerUnitLocalCurrencyNum &&
        (() => {
          const { asCurrency: formattedAmount } = medianListPricePerUnitLocalCurrencyNum;
          return (
            <QuickFact themeColor="pink" stickerImageSrc={quickFactBackgroundDoodle}>
              {renderMedianCostFact(formattedAmount)}
            </QuickFact>
          );
        })()}

      <SeoLocationReviewsCarousel
        seoLocation={seoLocation}
        reviews={reviews}
        searchFilters={initialSearchFilters}
        language={language}
        // DEV-144415 - once default is updated remove this prop
        headingVariant="heading"
        headingSize="400"
      />
      <SeoLocationRoverProtect />
      <Section my={['8x', '16x']}>
        {/* DEV-144415 move url logic to inside the component */}
        <WhyChooseBrand supportUrl={countryConfig.urls.supportUrl} />
      </Section>
      <SeoHowBrandWorks
        serviceTypeName={serviceTypeName}
        providerNounPlural={internalServiceType.providerNounPlural}
        petType={(petType as PetType) ?? undefined}
      />
      <Section my={['8x', '16x']}>
        <ValuePropFilterOptions
          locationName={locationName}
          language={language}
          searchFilters={initialSearchFilters}
          acceptsPuppies={petType !== PetType.CAT}
          onPress={onValuePropFilterOptionsCtaClick(serviceTypeSlug)}
        />
      </Section>

      {avgRepeatCustomerAmongSittersWithRepeatNum &&
        (() => {
          const { asNumber: clientCount } = avgRepeatCustomerAmongSittersWithRepeatNum;
          return (
            <QuickFact themeColor="cyan" stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Sitters in {locationName} average{' '}
                <QuickFactHighlight>{clientCount}</QuickFactHighlight> repeat {serviceTypeName}{' '}
                clients
              </Trans>
            </QuickFact>
          );
        })()}
      <SeoLocationFaqList
        seoLocationDescriptor={seoLocationDescriptor}
        localityType={localityType}
        internalServiceTypeName={internalServiceType.name}
        locationName={locationName}
      />
      <Section mt={['8x', '16x']}>
        <StyledSeoText
          data-qa-id="search-location-text"
          dangerouslySetInnerHTML={{
            __html: details.text,
          }}
        />
      </Section>
      <Section mt={['8x', '16x']}>
        <AppStoreCallout />
      </Section>
      <SeoLocationFooterLinks
        breadcrumbData={breadcrumbData}
        serviceName={details.serviceTypeName}
        locationName={cityName}
        linkedLocations={linkedLocations}
        relatedServices={relatedServices}
        internalServiceTypeSlug={internalServiceType.slug}
      />
    </SeoLocationPageWrapper>
  );
};

export default SeoLocationPageTemplate;
